import * as React from 'react';
import * as styles from './build-segment.module.scss';
import AttributeRow from './attribute-row';
import useAttributes from '../../../hooks/useSegmentAttributes';
import AttributeMenu from './attribute-menu';
import { useBuildSegment } from './useBuildSegment';
import AddAttribute from './add-attribute-button';
import SegmentIncludeExcludeHeader from '../segment-include-exclude-header';
import { findBaseAttributeId } from '../../../utilities/attribute-filter';

interface Props {
  isNew?: boolean;
  exclude?: boolean;
  isDependency?: boolean;
}

/**
 * Main component for the Build Segment section of the Create New Segment view, renders the rows of attribute
 * select and filter inputs and the attribute selection dialog.
 */
const BuildSegment = ({ isNew, exclude, isDependency }: Props) => {
  const { selectedAttributes, dispatch } = useBuildSegment(exclude);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const attributesQuery = useAttributes();

  /** Prevent select input from opening default menu, open alternative dialog */
  const handleOpen = () => {
    setDialogOpen(true);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  /** Update `selectedAttributes`, maintaining the order of `checked.current` and any previously selected filter options */
  const handleApply = (checked: number[]) => {
    if (checked.length === 1) {
      dispatch({ type: 'applyAttributes', checked, attributes: attributesQuery.data.attributes, position: [] });
    }
    if (checked.length > 1) {
      dispatch({
        type: 'addNewBaseGroup',
        newGroupAttributeIds: checked,
        attributes: attributesQuery.data.attributes
      });
    }
    setDialogOpen(false);
  };

  /** Remove a single attribute from both selected attributes via delete icon */
  const handleDelete = (attributeId: number, attributeIndex: number) => {
    dispatch({ type: 'deleteAttribute', attributeId, position: [], attributeIndex });
  };

  const emptyAttributes = (buildAttributes) => {
    if ('predicates' in buildAttributes) {
      return buildAttributes.predicates.every(emptyAttributes);
    } else {
      return buildAttributes.length === 0;
    }
  };

  return (
    <React.Fragment>
      <SegmentIncludeExcludeHeader isNew={isNew} exclude={exclude} />
      <div className={styles.buildSegmentRoot}>
        {selectedAttributes?.predicates.every(emptyAttributes) ? (
          <AttributeRow
            required={true}
            handleOpen={handleOpen}
            handleDelete={handleDelete}
            order={0}
            exclude={exclude}
            isDependency={isDependency}
          />
        ) : (
          selectedAttributes?.predicates.map((predicate, idx) => (
            <AttributeRow
              key={`attribute-row-${findBaseAttributeId(predicate)}-${idx}`}
              predicate={predicate}
              required={idx === 0}
              handleOpen={handleOpen}
              handleDelete={handleDelete}
              order={idx}
              exclude={exclude}
              isDependency={isDependency}
            />
          ))
        )}
        {selectedAttributes?.predicates.length > 0 && <AddAttribute label="Add Group" handleClick={handleOpen} />}
        {selectedAttributes && <AttributeMenu open={dialogOpen} onCancel={handleCancel} onApply={handleApply} />}
      </div>
    </React.Fragment>
  );
};

export default React.memo(BuildSegment);
